import { useState, type ReactNode } from 'react';
import type { Photo as PhotoAsset } from '@/content/ownerMedia';
import { useI18n } from '@/i18n/context';
import styles from './Photo.module.css';

/**
 * A real photograph from the registry (src/content/ownerMedia.ts). If an
 * externally hosted image fails to load (or the build carries none), it
 * renders `fallback` — by default a quiet tinted surface; pass `null` to
 * render nothing — instead of a broken-image icon, never a placeholder
 * drawing.
 */
export function Photo({
  photo,
  className,
  sizes = '100vw',
  priority = false,
  decorative = false,
  fallback,
  onFail,
}: {
  photo: PhotoAsset | null | undefined;
  className?: string;
  sizes?: string;
  priority?: boolean;
  /** Purely atmospheric use (text alongside carries the meaning): empty alt. */
  decorative?: boolean;
  /** Shown when there is no photo or it fails to load; `null` hides the frame. Defaults to a tinted surface. */
  fallback?: ReactNode;
  /** Called once if the image fails to load (lets a caller drop a caption with it). */
  onFail?: () => void;
}) {
  const { language } = useI18n();
  const [failed, setFailed] = useState(false);
  if (!photo || failed) {
    if (fallback !== undefined) return <>{fallback}</>;
    return <span className={[styles.empty, className ?? ''].join(' ')} aria-hidden="true" />;
  }
  return (
    <img
      src={photo.src}
      srcSet={photo.srcSet}
      sizes={photo.srcSet ? sizes : undefined}
      width={photo.width}
      height={photo.height}
      alt={decorative ? '' : language === 'ar' ? photo.altAr : photo.altEn}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      referrerPolicy={photo.external ? 'no-referrer' : undefined}
      className={[styles.photo, className ?? ''].join(' ')}
      onError={() => {
        setFailed(true);
        onFail?.();
      }}
      data-asset-class={photo.classification}
    />
  );
}
